<template>
  <DolphinTable :columns="columns" :rows="rows">
    <template #id="{ value }">
      <button
        :class="[
          $style.button,
          { [$style['button--selected']]: value === filteringPlannerId },
        ]"
        @click="onClickPlanner(value)"
        type="button"
      >
        {{ plannerMap[value]?.name ?? 'error' }}
      </button>
    </template>
  </DolphinTable>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent } from 'vue'
import type { Column } from '@/components/organisms/DolphinTable/DolphinTable.vue'
import DolphinTable from '@/components/organisms/DolphinTable/DolphinTable.vue'
import { COMPARE_FUNCTION, safeCastToInteger } from '@/libs/utilities/sort'
import type { PlannerID } from '@/types/app'
import type {
  MatchSummaryTablePlannerFragment,
  MatchSummaryTableMatchStatusFragment,
} from './MatchSummaryTable.generated'

export type PlannerMap = Record<PlannerID, MatchSummaryTablePlannerFragment>
export const createPlannerMap = (
  planners: readonly MatchSummaryTablePlannerFragment[]
): PlannerMap => {
  const map: PlannerMap = {}
  for (const planner of planners) {
    const plannerID = Number.parseInt(planner.id, 10)
    map[plannerID] = planner
  }
  return map
}

type Row = {
  id: string
  matches: number
  qrMatches: number
  onlineMatches: number
  talks: number
}
type RowMap = Record<string, Row>

export default defineComponent({
  name: 'MatchSummaryTable',
  components: {
    DolphinTable,
  },
  props: {
    statuses: {
      type: Array as PropType<MatchSummaryTableMatchStatusFragment[]>,
      required: true,
    },
    plannerMap: {
      type: Object as PropType<PlannerMap>,
      required: true,
    },
    filteringPlannerId: {
      type: Number as PropType<Maybe<PlannerID>>,
    },
  },
  computed: {
    columns(): Column<unknown>[] {
      return [
        {
          label: '名前',
          key: 'id',
          compareFunction: (oneId: unknown, nextId: unknown): number =>
            COMPARE_FUNCTION.STRING(
              this.plannerMap[safeCastToInteger(oneId)]?.name,
              this.plannerMap[safeCastToInteger(nextId)]?.name
            ),
        },
        {
          label: '総マッチング数',
          key: 'matches',
          compareFunction: COMPARE_FUNCTION.INTEGER,
        },
        {
          label: 'QRコードからのマッチ数',
          key: 'qrMatches',
          compareFunction: COMPARE_FUNCTION.INTEGER,
        },
        {
          label: 'オンラインマッチ数',
          key: 'onlineMatches',
          compareFunction: COMPARE_FUNCTION.INTEGER,
        },
        {
          label: '有効チャット数',
          key: 'talks',
          compareFunction: COMPARE_FUNCTION.INTEGER,
        },
      ]
    },
    rows(): Row[] {
      const map = this.statuses.reduce<RowMap>(
        (
          rowMap: RowMap,
          status: MatchSummaryTableMatchStatusFragment
        ): RowMap => {
          if (rowMap[status.plannerId] === undefined) {
            rowMap[status.plannerId] = {
              id: status.plannerId,
              matches: 0,
              qrMatches: 0,
              onlineMatches: 0,
              talks: 0,
            }
          }
          rowMap[status.plannerId].matches += 1
          rowMap[status.plannerId].qrMatches += Number(
            status.inflowSource === 'qr'
          )
          rowMap[status.plannerId].onlineMatches += Number(
            status.inflowSource === 'normal'
          )
          rowMap[status.plannerId].talks += Number(status.hasTalked)
          return rowMap
        },
        {}
      )

      // eslint-disable-next-line fp/no-mutating-methods
      return Object.values(map).sort((one: Row, next: Row): number =>
        COMPARE_FUNCTION.INTEGER(one.id, next.id)
      )
    },
  },
  methods: {
    onClickPlanner(plannerId: PlannerID) {
      this.$emit('selectPlanner', plannerId)
    },
  },
})
</script>

<style lang="scss" module>
.button {
  font-weight: bold;
  outline: 0;
  transition: color $transition-interaction;

  &:hover {
    color: $color-text-and-object-low-emphasis;
  }

  &--selected {
    color: $color-text-and-object-accent-primary;

    &:hover {
      color: $color-text-and-object-accent-primary;
    }
  }
}
</style>
